import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { nf, pf, pct, signed, money } from "../lib/format";
import { statusMeta, pfColor } from "../lib/status";
import { Loader2, Check, X } from "lucide-react";

const Row = ({ label, value, accent }) => (
  <div className="flex justify-between py-1.5 border-b border-slate-100 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className={`font-mono font-medium ${accent || "text-slate-900"}`}>{value}</span>
  </div>
);

export default function CostCodeDetail({ projectId, code, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    api.get(`/projects/${projectId}/cost-codes/${encodeURIComponent(code)}/detail`).then((r) => setD(r.data));
  }, [projectId, code]);

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="cost-code-detail">
        <SheetHeader>
          <SheetTitle className="font-head text-xl">
            <span className="font-mono">{code}</span>
            {d?.setup && <span className="text-slate-500 font-sans text-base ml-2">{d.setup.description}</span>}
          </SheetTitle>
          <SheetDescription>Budget, changes, weekly production, calculation breakdown, and audit history for this cost code.</SheetDescription>
        </SheetHeader>

        {!d ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#0B192C]" /></div>
        ) : (
          <div className="space-y-6 mt-4">
            {d.computed && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <StatusBadge status={d.computed.status} />
                  <span className={`font-mono font-bold text-lg ml-auto ${pfColor(d.computed.productivity_factor)}`}>
                    PF {pf(d.computed.productivity_factor)}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-x-6">
                  <div>
                    <SectionTitle>Budget & Revised</SectionTitle>
                    <Row label="Original Qty" value={`${nf(d.computed.original_qty, 0)} ${d.setup?.uom || ""}`} />
                    <Row label="Original Hrs" value={nf(d.computed.original_hours)} />
                    <Row label="Change Qty (incl.)" value={nf(d.computed.change_qty, 0)} />
                    <Row label="Change Hrs (incl.)" value={nf(d.computed.change_hours)} />
                    <Row label="Revised Qty" value={nf(d.computed.revised_qty, 0)} />
                    <Row label="Revised Hrs" value={nf(d.computed.revised_hours)} />
                    <Row label="Revised U/Hr" value={nf(d.computed.revised_production_rate, 2)} />
                  </div>
                  <div>
                    <SectionTitle>Actual & Forecast</SectionTitle>
                    <Row label="Installed Qty" value={nf(d.computed.installed_qty, 0)} />
                    <Row label="ST / OT Hrs" value={`${nf(d.computed.st_hours)} / ${nf(d.computed.ot_hours)}`} />
                    <Row label="Actual Hrs" value={nf(d.computed.actual_hours)} />
                    <Row label="Earned Hrs" value={nf(d.computed.earned_hours)} />
                    <Row label="% Complete" value={pct(d.computed.percent_complete)} />
                    <Row label="Forecast Hrs" value={nf(d.computed.forecast_hours)} />
                    <Row label="Forecast +/-" value={signed(d.computed.forecast_gain_loss)} />
                    <Row label="JTD Hrs +/-" value={signed(d.computed.jtd_hours_gain_loss)} />
                    <Row label="JTD $ +/-" value={money(d.computed.jtd_dollar_gain_loss)} />
                  </div>
                </div>
              </div>
            )}

            <div>
              <SectionTitle>Changes ({d.changes.length})</SectionTitle>
              {d.changes.length === 0 ? <Empty /> : d.changes.map((c) => (
                <div key={c.id} className="flex items-center gap-2 py-1.5 border-b border-slate-100 text-sm">
                  <span className="font-mono text-xs">{c.change_id || "—"}</span>
                  <span className="text-slate-600 flex-1 truncate">{c.description}</span>
                  <span className="font-mono">{signed(c.qty_adjustment, 0)}q</span>
                  <span className="font-mono">{signed(c.hour_adjustment)}h</span>
                  {c.include_in_forecast
                    ? <span className="text-emerald-600 flex items-center text-xs"><Check className="w-3 h-3" />incl</span>
                    : <span className="text-slate-400 flex items-center text-xs"><X className="w-3 h-3" />excl</span>}
                </div>
              ))}
            </div>

            <div>
              <SectionTitle>Weekly Production ({d.actuals.length})</SectionTitle>
              {d.actuals.length === 0 ? <Empty /> : (
                <table className="w-full text-sm">
                  <thead><tr className="text-xs text-slate-500 text-left">
                    <th className="py-1">Week</th><th className="text-right">Installed</th>
                    <th className="text-right">ST</th><th className="text-right">OT</th>
                  </tr></thead>
                  <tbody>
                    {d.actuals.map((a) => (
                      <tr key={a.id} className="border-t border-slate-100">
                        <td className="py-1 font-mono">{a.week_ending}</td>
                        <td className="text-right font-mono">{nf(a.installed_qty, 0)}</td>
                        <td className="text-right font-mono">{nf(a.st_hours)}</td>
                        <td className="text-right font-mono">{nf(a.ot_hours)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div>
              <SectionTitle>Audit History ({d.audit.length})</SectionTitle>
              {d.audit.length === 0 ? <Empty /> : d.audit.slice(0, 20).map((a) => (
                <div key={a.id} className="py-1.5 border-b border-slate-100 text-xs text-slate-600">
                  <span className="font-medium uppercase text-[#E8791A]">{a.action}</span> {a.record_type} ·{" "}
                  {a.changed_by} · {new Date(a.timestamp).toLocaleString()}
                </div>
              ))}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

const SectionTitle = ({ children }) => (
  <h4 className="font-head text-sm font-semibold uppercase tracking-wide text-slate-700 mb-1 mt-2">{children}</h4>
);
const Empty = () => <div className="text-sm text-slate-400 py-2">None recorded.</div>;
const StatusBadge = ({ status }) => {
  const m = statusMeta(status);
  return <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${m.badge}`}>
    <span className="w-1.5 h-1.5 rounded-full" style={{ background: m.dot }} />{status}</span>;
};
